/**
 * BathroomsDetailViewCommon Component
 *
 * This component is used to display the common elements of a bathrooms detail screen across different platforms.
 * It handles data display such as the bathrooms's name, distance, accessibility, and hours of operation.
 *
 * Props:
 *  - route: Navigation route that includes bathrooms details.
 *  - FontAwesomeIcon: The FontAwesome icon component used for displaying icons.
 *  - iconSize: The size of the FontAwesome icon.
 *
 * @file BathroomsDetailViewCommon.js
 * @module BathroomsDetailViewCommon
 */
import React, { useState, useEffect, useMemo } from "react";
import { View, StyleSheet, Dimensions, ScrollView, Platform } from "react-native";
import { useNavigation } from "@react-navigation/native";
import Chip from "../../../components/Chip/Chip";
import { COLORS, STRINGS } from "../../../constants";
import {
  faBan,
  faDirections,
  faLocationDot,
  faShareAlt,
  faWheelchair,
  faSnowflake,
  faSun,
  faLeaf,
} from "@fortawesome/free-solid-svg-icons";
import Heading from "../../../components/Heading/Heading";
import DetailButtonArray from "../../../components/DetailButtonArray/DetailButtonArray";
import DetailInfoCard from "../../../components/DetailInfoCard/DetailInfoCard";
import DetailsMapView from "../../Other/DetailsMapView/DetailsMapView";
import BathroomsStore from "../../../storage/bathroomsStore";
import { getOpenSeasons, openDirections, share, supportsShare } from "../../../utils/helper";
import { getCurrentLocation, getDistanceFromLocation } from "../../../utils/geolocation";
import { formatBathroomHours } from "../../../utils/formatting";
import Loading from "../../../components/Loading/Loading";
import PageNotFound from "../../../components/NoResults/PageNotFound";
import { logAnalyticsEvent } from "../../../utils/firebase";
import useMetaDescriptionOnFocus from "../../../components/Meta/MetaDescription";

const BathroomsDetailViewCommon = ({ route, FontAwesomeIcon, iconSize }) => {
  logAnalyticsEvent("screen_view", { name: "VBGo-BathroomsDetailView" });

  const navigation = useNavigation();
  const [distance, setDistance] = useState(null);
  const bathroomData = BathroomsStore((state) => state.bathrooms);
  const bathroom = bathroomData[route.params?.index];
  const loadingBathrooms = BathroomsStore((state) => state.loadingBathrooms);
  useMetaDescriptionOnFocus(bathroom?.attributes?.Location);

  const openSeasons = useMemo(() => {
    return bathroom ? getOpenSeasons(bathroom.attributes) : {};
  }, [bathroom]);

  const chips = useMemo(() => {
    if (!bathroom) return [];
    return [
      {
        title: distance != null ? `${distance} ${STRINGS.milesAway}` : null,
        icon: faLocationDot,
        backgroundColor: COLORS.backgroundColor,
        borderColor: COLORS.border,
        iconColor: COLORS.textDark,
      },
      {
        title: openSeasons?.chipText,
        icon: openSeasons?.chipIcon,
        backgroundColor: openSeasons?.chipBackgroundColor,
        borderColor: openSeasons?.chipBorderColor,
        iconColor: openSeasons?.chipIconColor,
      },
      {
        title: bathroom.attributes?.ada_access === "Y" ? STRINGS.accessible : STRINGS.notAccessible,
        icon: bathroom.attributes?.ada_access === "Y" ? faWheelchair : faBan,
        backgroundColor: bathroom.attributes?.ada_access === "Y" ? COLORS.blueBackground : COLORS.warningLight,
        borderColor: bathroom.attributes?.ada_access === "Y" ? COLORS.blueBorder : COLORS.warning,
        iconColor: bathroom.attributes?.ada_access === "Y" ? COLORS.secondary : COLORS.warning,
      },
    ];
  }, [bathroom, distance, openSeasons]);

  const renderChips = useMemo(() => {
    return chips.map((chip, index) =>
      chip.title ? (
        <Chip
          key={index}
          FontAwesomeIcon={FontAwesomeIcon}
          icon={chip.icon}
          title={chip.title}
          size={iconSize}
          backgroundColor={chip.backgroundColor}
          borderColor={chip.borderColor}
          iconColor={chip.iconColor}
          style={styles.chip}
        />
      ) : null
    );
  }, [chips, FontAwesomeIcon, iconSize]);

  useEffect(() => {
    const getDistance = async () => {
      if (!bathroom) return;
      try {
        const userLocation = await getCurrentLocation();
        setDistance(
          await getDistanceFromLocation(userLocation, {
            lat: bathroom?.latitude || bathroom?.geometry?.y,
            lng: bathroom?.longitude || bathroom?.geometry?.x,
          })
        );
      } catch (err) {
        return null;
      }
    };

    getDistance();
  }, [bathroom]);

  useEffect(() => {
    if (bathroom) {
      navigation.setOptions({
        title: bathroom?.attributes?.Location,
        headerRight: () => (
          <DetailButtonArray
            FontAwesomeIcon={FontAwesomeIcon}
            buttons={initHeaderButtons()}
            onPress={handlePress}
            iconSize={Platform.OS === "web" ? "2x" : iconSize}
          />
        ),
        headerRightContainerStyle: styles.headerRight,
        headerTitleStyle: styles.headerTitle,
      });
    }
  }, [bathroom]);

  /**
   * handlePress
   *
   * This function is a simple handler for button press events. It logs the label of the pressed
   * button to the console for debugging or action tracking purposes.
   */
  const handlePress = (button) => {
    if (button.label === "Directions") {
      openDirections(bathroom.latitude || bathroom.geometry.y, bathroom.longitude || bathroom.geometry.x);
    }
    if (button.label === "Share") {
      const url = `\n\n${process.env.REACT_APP_APPURL}/bathrooms/details?index=${route.params?.index}`;
      const message = `${bathroom.attributes.Location} Bathroom: ${url}`;
      share(message);
    }
  };
  if (loadingBathrooms) {
    return (
      <View style={styles.container}>
        <Loading />
      </View>
    );
  }

  if (!bathroom) {
    return (
      <View style={styles.container}>
        <PageNotFound />
      </View>
    );
  }

  /**
   * Initializes an array of two header buttons for directions and sharing.
   *
   * Each button object may include:
   * - Directions: If latitude and longitude are present.
   * - Share: If sharing is supported.
   *
   * @returns {Array<Object>} Array of button configuration objects for the header.
   */
  const initHeaderButtons = () => {
    const buttons = [{ icon: faDirections, label: "Directions", color: COLORS.border }];

    if (supportsShare()) {
      buttons.push({ icon: faShareAlt, label: "Share", color: COLORS.border });
    }

    return buttons;
  };

  return (
    <ScrollView contentContainerStyle={styles.scrollContainer}>
      <View style={styles.container}>
        <View style={styles.mapContainer}>
          <DetailsMapView
            name={bathroom.attributes.Location}
            lat={bathroom.latitude || bathroom.geometry.y}
            lng={bathroom.longitude || bathroom.geometry.x}
            screen={route.name}
          />
        </View>
        <Heading text={bathroom?.attributes?.Location} fontSize={26} />
        <ScrollView
          horizontal
          showsHorizontalScrollIndicator={false}
          contentContainerStyle={styles.chipContainer}
          style={styles.chipScrollView}
        >
          {renderChips}
        </ScrollView>
        <View style={styles.detailsContainer}>
          <DetailInfoCard
            title={STRINGS.winterHours}
            subtitle={formatBathroomHours(bathroom.attributes.winterhours)}
            FontAwesomeIcon={FontAwesomeIcon}
            iconName={faSnowflake}
            iconSize={Platform.OS === "web" ? iconSize : 20}
          />
          <DetailInfoCard
            title={STRINGS.springHours}
            subtitle={formatBathroomHours(bathroom.attributes.springhours)}
            FontAwesomeIcon={FontAwesomeIcon}
            iconName={faLeaf}
            iconSize={Platform.OS === "web" ? iconSize : 20}
          />
          <DetailInfoCard
            title={STRINGS.summerHours}
            subtitle={formatBathroomHours(bathroom.attributes.summerhours)}
            FontAwesomeIcon={FontAwesomeIcon}
            iconName={faSun}
            iconSize={Platform.OS === "web" ? iconSize : 20}
            showDivider={false}
          />
        </View>
      </View>
    </ScrollView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  scrollContainer: {
    flexGrow: 1,
    justifyContent: "flex-start",
    paddingBottom: Dimensions.get("window").height * 0.1,
  },
  chipContainer: {
    flexDirection: "row",
    gap: 8,
    paddingTop: 12,
  },
  chipScrollView: {
    flexGrow: 0,
  },
  chip: {
    marginRight: 8,
  },
  detailsContainer: {
    marginBottom: 24,
  },
  mapContainer: {
    height: Platform.OS === "web" ? "300px" : 260,
    marginBottom: 10,
  },
  headerRight: {
    flexDirection: "row",
    alignItems: "center",
    paddingRight: 8,
    justifyContent: "flex-end",
  },
  headerTitle: {
    display: "none",
  },
});

export default BathroomsDetailViewCommon;
